import { EXTEND, IS, IS_NOT, nil, NO, UIColor, UIRoute, UIView, UIViewBroadcastEvent, UIViewController, YES } from "uicore-ts"
import { RHCore } from "../Core/RHCore"
import { RHColor } from "../Custom components/RH/RHColor"
import { RHPanelView } from "../Custom components/RH/RHPanelView"
import { RHVerticalStackView } from "../Custom components/RH/RHVerticalStackView"
import { RHSpacing } from "../Custom components/RH/Utils_slop/RHDesignTokens"
import { RHCompanyViewTokens } from "../Custom components/RH/Utils_slop/RHViewTokens"
import { CompanyAdministrationViewController } from "./CompanyAdministration/CompanyAdministrationViewController"
import { CompanyAffiliationViewController } from "./CompanyAffiliation/CompanyAffiliationViewController"
import { CompanyTenderInterestViewController } from "./CompanyTenderInterestViewController"


export class CompanyViewController extends UIViewController {

    static override readonly routeComponentName = "company"
    static override readonly ParameterIdentifierName = {
        tab: "tab",
        companyId: "companyId",
        workflowId: "workflowId",
        sourceNewProcurement: "sourceNewProcurement"
    }


    static tabInRoute(route: UIRoute): string {
        return route.componentWithViewController(CompanyViewController)
                ?.parameters[CompanyViewController.ParameterIdentifierName.tab]
            ?? "affiliation"
    }


    static workflowIdInRoute(route: UIRoute): string | undefined {
        return route.componentWithViewController(CompanyViewController)
            ?.parameters[CompanyViewController.ParameterIdentifierName.workflowId]
    }


    static companyIdInRoute(route: UIRoute): string | undefined {
        return route.componentWithViewController(CompanyViewController)
            ?.parameters[CompanyViewController.ParameterIdentifierName.companyId]
    }


    // One-shot "invite a member" deep link, set only by
    // ProcurementDetailsViewController._routeForCompanyMemberInvitation.
    static wasInvitationRequestedInRoute(route: UIRoute): boolean {
        return route.componentWithViewController(CompanyViewController)
            ?.parameters.action === "inviteMember"
    }


    static isMemberInvitationFromNewProcurementInRoute(route: UIRoute): boolean {
        return route.componentWithViewController(CompanyViewController)
            ?.parameters[CompanyViewController.ParameterIdentifierName.sourceNewProcurement] === "1"
    }


    // Hosts the combined administration stack or the offerer-only CPV settings view.
    readonly contentPanelView = new RHPanelView().addedAsSubviewToView(this.view).configuredWithObject({
        backgroundColor: UIColor.whiteColor,
        layoutSubviews: EXTEND(() => {
            if (this.contentPanelView.subviews.firstElement) {
                this.contentPanelView.subviews.firstElement.frame = this.contentPanelView.bounds.rectangleWithHeight(
                    this.contentPanelView.subviews.firstElement
                )
            }
        })
    })

    readonly administrationStackView = new RHVerticalStackView().configuredWithObject({
        rowGap: RHSpacing.small
    })


    // ── Child view controllers — lazily initialized ──

    _affiliationViewController?: CompanyAffiliationViewController
    _administrationViewController?: CompanyAdministrationViewController
    _tenderInterestViewController?: CompanyTenderInterestViewController

    get affiliationViewController(): CompanyAffiliationViewController {
        if (IS_NOT(this._affiliationViewController)) {
            this._affiliationViewController = new CompanyAffiliationViewController(
                new UIView("CompanyAffiliationView")
            )
            this._affiliationViewController.workflowLinkWasConsumed = () => {
                this._removeWorkflowIdFromCurrentRoute()
            }
        }
        return this._affiliationViewController
    }

    get administrationViewController(): CompanyAdministrationViewController {
        if (IS_NOT(this._administrationViewController)) {
            this._administrationViewController = new CompanyAdministrationViewController(
                new UIView("CompanyAdministrationView")
            )
            this._administrationViewController.workflowLinkWasConsumed = () => {
                this._removeWorkflowIdFromCurrentRoute()
            }
            this._administrationViewController.invitationLinkWasConsumed = () => {
                this._removeInvitationRequestFromCurrentRoute()
            }
        }
        return this._administrationViewController
    }

    get tenderInterestViewController(): CompanyTenderInterestViewController {
        if (IS_NOT(this._tenderInterestViewController)) {
            this._tenderInterestViewController = new CompanyTenderInterestViewController(
                new UIView("CompanyTenderInterestView")
            )
        }
        return this._tenderInterestViewController
    }


    constructor(view: UIView) {
        super(view)
        this.view.configureWithObject({ backgroundColor: RHColor.background })
    }


    override async handleRoute(route: UIRoute) {
        await super.handleRoute(route)
        const companyId = CompanyViewController.companyIdInRoute(route)
        const workflowId = CompanyViewController.workflowIdInRoute(route)
        const tab = CompanyViewController.tabInRoute(route)
        const requestsMemberInvitation =
            CompanyViewController.wasInvitationRequestedInRoute(route)
        let reloadsCompanyAffiliations = NO
        if (workflowId) {
            reloadsCompanyAffiliations = YES
        }
        await RHCore.sharedInstance.companyAffiliationStore.retrieveMyCompanyAffiliations(
            reloadsCompanyAffiliations
        )
        if (companyId) {
            const companyAffiliation = RHCore.sharedInstance.companyAffiliationStore.affiliations.find(affiliation =>
                affiliation.company?._id?.toString() === companyId
            )
            const canActivateCompany =
                companyAffiliation &&
                companyAffiliation.source !== "relationship" &&
                companyAffiliation.membershipStatus === "active"
            if (canActivateCompany) {
                RHCore.sharedInstance.companyAffiliationStore.deactivateDefaultAllCompaniesScope()
                RHCore.sharedInstance.companyAffiliationStore.activateCompany(companyId)
            }
        }
        await this._mountChildViewControllerForTab(
            tab,
            companyId,
            workflowId,
            requestsMemberInvitation
        )
    }


    override viewDidReceiveBroadcastEvent(event: UIViewBroadcastEvent) {
        super.viewDidReceiveBroadcastEvent(event)
        // CPV settings exist only in offerer mode; leaving that mode while on the
        // CPV tab would strand the user, so fall back to the company card.
        if (event.name === RHCore.broadcastEventName.applicationModeDidChange) {
            if (this._tenderInterestIsMounted()
                && !RHCore.sharedInstance.isInOffererMode) {
                this._routeToTab("administration")
            }
        }
        if (
            (
                event.name === RHCore.broadcastEventName.companyAffiliationsDidChange ||
                event.name === RHCore.broadcastEventName.activeCompanyDidChange ||
                event.name === RHCore.broadcastEventName.currentCompanyAffiliationDidChange
            ) &&
            IS(this.administrationStackView.superview)
        ) {
            if (event.name === RHCore.broadcastEventName.activeCompanyDidChange) {
                this._clearRequestedWorkflowScope()
            }
            this._mountAdministrationViewControllers().then(nil)
        }
    }


    async _mountChildViewControllerForTab(
        tab: string,
        companyId?: string,
        workflowId?: string,
        requestsMemberInvitation: boolean = NO
    ) {
        // CPV settings are offerer-only; all other routes use the administration overview.
        if (tab === "cpvInterests" && RHCore.sharedInstance.isInOffererMode) {
            await this._mountChildViewController(this.tenderInterestViewController)
        }
        else {
            this.affiliationViewController.requestedCompanyId = companyId
            this.administrationViewController.requestedCompanyId = companyId
            if (tab === "administration") {
                this.affiliationViewController.requestedWorkflowId = undefined
                this.administrationViewController.requestedWorkflowId = workflowId
                this.administrationViewController.requestsMemberInvitation = requestsMemberInvitation
            }
            else {
                this.affiliationViewController.requestedWorkflowId = workflowId
                this.administrationViewController.requestedWorkflowId = undefined
                this.administrationViewController.requestsMemberInvitation = NO
            }
            await this._mountAdministrationViewControllers()
        }
        this.view.setNeedsLayoutUpToRootView()
    }


    _routeToTab(tab: string) {
        UIRoute.currentRoute
            .routeWithComponent(
                CompanyViewController.routeComponentName,
                { [CompanyViewController.ParameterIdentifierName.tab]: tab },
                YES
            )
            .apply()
    }


    _removeWorkflowIdFromCurrentRoute() {
        const currentRoute = UIRoute.currentRoute
        if (!CompanyViewController.workflowIdInRoute(currentRoute)) {
            return
        }
        this._clearRequestedWorkflowScope()
        currentRoute
            .routeByRemovingParameterInComponent(
                CompanyViewController.routeComponentName,
                CompanyViewController.ParameterIdentifierName.workflowId
            )
            .routeByRemovingParameterInComponent(
                CompanyViewController.routeComponentName,
                CompanyViewController.ParameterIdentifierName.companyId
            )
            .applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
    }


    _removeInvitationRequestFromCurrentRoute() {
        const currentRoute = UIRoute.currentRoute
        if (!CompanyViewController.wasInvitationRequestedInRoute(currentRoute)) {
            return
        }
        currentRoute
            .routeByRemovingParameterInComponent(CompanyViewController.routeComponentName, "action")
            .applyByReplacingCurrentRouteInHistoryWithoutRouteChangeApproval()
    }


    _clearRequestedWorkflowScope() {
        if (this._affiliationViewController) {
            this._affiliationViewController.requestedCompanyId = undefined
            this._affiliationViewController.requestedWorkflowId = undefined
        }
        if (this._administrationViewController) {
            this._administrationViewController.requestedCompanyId = undefined
            this._administrationViewController.requestedWorkflowId = undefined
        }
    }


    _mountedChildViewControllers(): UIViewController[] {
        return ([
            this._affiliationViewController,
            this._administrationViewController,
            this._tenderInterestViewController
        ] as Array<UIViewController | undefined>).filter(
            (viewController): viewController is UIViewController =>
            IS(viewController) && this.hasChildViewController(viewController)
        )
    }


    _tenderInterestIsMounted(): boolean {
        const viewController = this._tenderInterestViewController
        return IS(viewController) && this.hasChildViewController(viewController)
    }


    async _mountChildViewController(viewController: UIViewController) {
        await this._mountChildViewControllers(
            [viewController],
            this.contentPanelView
        )
    }


    async _mountAdministrationViewControllers() {
        const viewControllers: UIViewController[] = [this.affiliationViewController]
        if (this._canAdministerActiveCompany()) {
            viewControllers.push(this.administrationViewController)
        }
        await this._mountChildViewControllers(
            viewControllers,
            this.administrationStackView
        )
        this.view.setNeedsLayoutUpToRootView()
    }


    _canAdministerActiveCompany(): boolean {
        const affiliation = RHCore.sharedInstance.companyAffiliationStore.activeCompanyAffiliation
        return (
            IS(affiliation) &&
            affiliation.source !== "relationship" &&
            affiliation.isAdministrator === YES
        )
    }


    async _mountChildViewControllers(
        viewControllers: UIViewController[],
        containerView: UIView
    ) {
        const mountedViewControllers = this._mountedChildViewControllers()
        for (const mountedViewController of mountedViewControllers) {
            if (!viewControllers.includes(mountedViewController)) {
                await mountedViewController.viewWillDisappear()
                this.removeChildViewController(mountedViewController)
            }
        }

        if (containerView === this.administrationStackView) {
            if (IS_NOT(this.administrationStackView.superview)) {
                this.administrationStackView.addedAsSubviewToView(this.contentPanelView)
            }
        }
        else {
            this.administrationStackView.removeFromSuperview()
        }

        for (const viewController of viewControllers) {
            if (!this.hasChildViewController(viewController)) {
                this.addChildViewControllerInContainer(viewController, containerView)
                await viewController.viewWillAppear()
                viewController.viewDidAppear().then(nil)
            }
            else if (viewController === this._tenderInterestViewController) {
                this.tenderInterestViewController.reloadTenderInterest().then(nil)
            }
        }
    }


    override layoutViewSubviews() {
        super.layoutViewSubviews()

        this.view
            .contentBoundsWithInset(RHSpacing.medium)
            .IF(this._tenderInterestIsMounted())
            .rectangleWithMaxWidth(RHCompanyViewTokens.tenderInterestContentMaxWidth, 0.5)
            .ELSE()
            .rectangleWithMaxWidth(RHCompanyViewTokens.administrationContentMaxWidth, 0.5)
            .ENDIF()
            .rectangleWithHeight(this.contentPanelView)
            .assignedAsFrameOfView(this.contentPanelView)
    }

}
